import { useContext, useEffect, useRef, useState, RefObject } from "react";
import { AssetsV1Service } from "../../../common/services/api/api";
import { IAsset, IAssetCreate } from "../models";
import { Loading } from "../../../common/components/loading/loading";
import Context from "../../../common/components/utils/context";
import { notifcation } from "../../../common/components/utils/notifications";
import { Themes } from "../../../common/enums/themes";
import { TableView } from "./assets/table";
import { CodeGenerator } from "./assets/generate";
import { AssetEdit } from "./assets/edit";

export const AdminMobileAsset = () => {
  const context = useContext(Context);
  const service = new AssetsV1Service();

  const [loading, setLoading] = useState(true);
  const [update, setUpdate] = useState(true);
  const [assets, setAssets] = useState<IAsset[]>([]);

  useEffect(() => {
    update && fetch().then(() => setUpdate(false));
  }, [update]);

  const fetch = async () => {
    try {
      setLoading(true);
      const response = await service.getMyAssets();
      setAssets(response.data.map((x: any) => x.asset));
    } catch (error) {
      console.error("Error fetching assets:", error);
    } finally {
      setLoading(false);
    }
  };

  const generate = (item: IAsset) => {
    context.setModal({
      show: true,
      component: <CodeGenerator item={item} onClose={onModalClose} />,
      size: "sm",
      onClose: onModalClose,
    });
  };

  const onModalClose = () => {
    context.setModal({ ...context.modal, show: false });
  };

  const save = async (item: IAsset) => {
    const asset: IAssetCreate = {
      description: item.description,
      manufacturer: item.manufacturer,
      model: item.model,
      serialNumber: item.serialNumber,
      yearManufactured: +item.yearManufactured,
      registrationNumber: item.registrationNumber,
      systemLink: item.systemLink,
      other: item.other,
    };
    try {
      const result = item.id
        ? await service.update(item.id, asset)
        : await service.create(asset);
      context.setAlert({
        message: result.data
          ? `Mobile asset ${item.id ? "updated" : "created"} successfully.`
          : "An error has occurred. Please try again or contact your administrator.",
        theme: result.data ? Themes.Success : Themes.Danger,
      });
      generate(result.data);
    } catch (error) {
      console.error(error);
    }
    setUpdate(true);
  };

  const edit = (item?: IAsset) => {
    const data = item ? item : ({} as IAsset);
    context.setModal({
      show: true,
      component: <AssetEdit {...data} save={save} />,
      size: "sm",
      onClose: () => context.setModal({ ...context.modal, show: false }),
      outsideClickToClose: false,
    });
  };

  const remove = async (item: IAsset) => {
    const confirm = await notifcation({
      title: "Confirm Delete",
      message: `Are your sure you want to delete ${item.description} ?`,
      cancel: "Cancel",
      confirm: "Delete",
    });
    if (confirm) {
      const response = await service.deleteAsset(item.id);
      if (response.status === 200) {
        setUpdate(true);
        context.setAlert({
          message: "Mobile asset deleted successfully!",
          theme: Themes.Success,
        });
      } else {
        context.setAlert({
          message: "Failed to delete asset!",
          theme: Themes.Danger,
        });
      }
    }
  };

  return loading ? (
    <Loading />
  ) : (
    <div className="container mb-auto relative">
      <div className="flex flex-col space-y-5">
        <div className="columns-2">
          <h2 className="text-2xl font-medium">Mobile Assets</h2>
          <div className="grid grid-flow-col justify-end space-x-4">
            {/* Future Bulk Delete Button Here */}
          </div>
        </div>
        <TableView
          assets={assets}
          generate={generate}
          remove={remove}
          edit={edit}
        />
        <div className="grid grid-flow-col justify-end">
          <button
            type="button"
            onClick={() => edit()}
            className="transition-all rounded-lg hover:text-white text-gray-800 text-sm tracking-wider font-medium outline-none bg-text-link px-10 py-2"
          >
            Add Mobile Asset
          </button>
        </div>
      </div>
    </div>
  );
};

export interface IAssetView {
  assets: IAsset[];
  generate: (item: IAsset) => void;
  edit: (item: IAsset) => void;
  remove: (item: IAsset) => void;
}
